/**
 * Analytics Bridge — GTM DataLayer integration for ShiftGears widget (#577)
 *
 * Thin module that pushes structured events to window.dataLayer
 * for the client's Google Tag Manager to pick up and route to GA4.
 *
 * No GA library dependency — just dataLayer.push().
 * Graceful no-op if GTM is not present.
 *
 * When the widget runs in iframe mode, events are forwarded
 * to the parent window via postMessage (using iframeEventEmitter).
 *
 * All events are prefixed with `sg_` (ShiftGears) to avoid
 * conflicts with the client's existing GTM events — except the lead
 * conversion (`trackLeadSubmitted`), which uses the ad agency's names and
 * also calls the Meta pixel.
 */

import { getIframeEventEmitter } from '../utils/iframeEventEmitter';
import { trackEvent as beaconTrackEvent } from './analyticsBeacon';
import { getCurrentCar } from './carEngagementEvents';

/** @type {boolean} Whether we're running inside an iframe */
let isIframe = false;

/** @type {string|null} Current session ID for tagging events */
let currentSessionId = null;

/** @type {string|null} Current organization ID */
let currentOrganizationId = null;

/**
 * Initialize the analytics bridge.
 * Call once during widget mount.
 *
 * @param {Object} options
 * @param {string} [options.sessionId] - Widget session ID
 * @param {string} [options.organizationId] - Organization ID
 */
export function initAnalytics({ sessionId, organizationId } = {}) {
  currentSessionId = sessionId || null;
  currentOrganizationId = organizationId || null;

  try {
    isIframe = window.self !== window.top;
  } catch {
    isIframe = true; // cross-origin iframe
  }
}

/**
 * Update session ID (e.g. when session is created after init).
 * @param {string} sessionId
 */
export function setAnalyticsSessionId(sessionId) {
  currentSessionId = sessionId;
}

/**
 * Hand a ready-made event object to the host page's GTM.
 *
 * In iframe mode, sends via postMessage to parent.
 * In standalone/widget mode, pushes directly to window.dataLayer.
 *
 * @param {Object} event - dataLayer event (with `event` property)
 */
function pushToDataLayer(event) {
  if (isIframe) {
    // Forward to parent window where GTM lives
    const emitter = getIframeEventEmitter();
    emitter.emitAnalyticsEvent(event);
  } else {
    // Push directly to dataLayer (creates array if not present)
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(event);
  }
}

/**
 * Push an analytics event to GTM dataLayer.
 *
 * @param {string} eventName - Event name (without sg_ prefix — added automatically)
 * @param {Object} [params={}] - Event parameters
 */
function pushEvent(eventName, params = {}) {
  try {
    pushToDataLayer({
      event: `sg_${eventName}`,
      sg_session_id: currentSessionId,
      sg_organization_id: currentOrganizationId,
      sg_timestamp: Date.now(),
      ...params,
    });

    // Duplicate to our backend via HTTP beacon (#589)
    beaconTrackEvent(`sg_${eventName}`, params);
  } catch {
    // Analytics should never crash the widget
  }
}

/**
 * Build the `currentCar` param block for chat-init events when the user is
 * viewing a specific car (#970). Empty object when no car is in context, so
 * spreading it is a no-op.
 * @returns {{ currentCar?: { carId: string, make: string|null, model: string|null } }}
 */
function currentCarParam() {
  const car = getCurrentCar();
  return car ? { currentCar: car } : {};
}

// ─── Public event methods ───────────────────────────────────

/**
 * Widget opened (from collapsed state).
 * @param {Object} params
 * @param {string} params.mode - 'voice' or 'chat'
 * @param {string} [params.launchContext] - Why the widget was opened
 */
export function trackWidgetOpen({ mode, launchContext }) {
  pushEvent('widget_open', {
    sg_mode: mode,
    sg_launch_context: launchContext?.action || 'direct_open',
    // Attribute the chat-init to the car on screen, if any (#970).
    ...currentCarParam(),
  });
}

/**
 * Widget closed (to collapsed state).
 * @param {Object} params
 * @param {string} params.previousMode - Mode before closing
 * @param {number|null} params.durationMs - Time widget was open (ms)
 */
export function trackWidgetClose({ previousMode, durationMs }) {
  pushEvent('widget_close', {
    sg_previous_mode: previousMode,
    sg_duration_ms: durationMs,
  });
}

/**
 * User switched between voice and text mode.
 * @param {Object} params
 * @param {string} params.from - Previous mode
 * @param {string} params.to - New mode
 */
export function trackModeSwitch({ from, to }) {
  pushEvent('mode_switch', {
    sg_from_mode: from,
    sg_to_mode: to,
  });
}

/**
 * Conversation started (first message sent or voice connected).
 * @param {Object} params
 * @param {string} params.mode - 'voice' or 'text'
 */
export function trackConversationStart({ mode }) {
  pushEvent('conversation_start', {
    sg_mode: mode,
    // Attribute the chat-init to the car on screen, if any (#970).
    ...currentCarParam(),
  });
}

/**
 * Conversation ended.
 * @param {Object} params
 * @param {number} [params.durationS] - Conversation duration in seconds
 * @param {string} [params.outcome] - Conversation outcome
 */
export function trackConversationEnd({ durationS, outcome }) {
  pushEvent('conversation_end', {
    sg_duration_s: durationS,
    sg_outcome: outcome,
  });
}

/**
 * Widget error occurred.
 * @param {Object} params
 * @param {string} params.errorType - Error category
 * @param {string} [params.message] - Error message (no PII)
 */
export function trackError({ errorType, message }) {
  pushEvent('error', {
    sg_error_type: errorType,
    sg_error_message: message,
  });
}

/**
 * WhatsApp button clicked.
 * Pushes to dataLayer AND calls gtag() if available for direct GA4 tracking.
 * @param {Object} params
 * @param {string} params.whatsappUrl - Full wa.me URL
 * @param {string} [params.context] - Where the click happened (e.g. 'car_card', 'contact_page', 'voice_widget')
 */
export function trackWhatsAppClick({ whatsappUrl, context }) {
  pushEvent('whatsapp_click', {
    sg_whatsapp_url: whatsappUrl,
    sg_click_context: context,
  });

  // Также вызываем gtag() напрямую, если клиент использует gtag.js вместо GTM
  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'whatsapp_button_click', {
        event_category: 'engagement',
        event_label: whatsappUrl,
        value: 1,
      });
    }
  } catch {
    // gtag вызов не должен ломать основную логику
  }
}

/**
 * Telegram button click (#1493) — mirror of trackWhatsAppClick.
 * @param {Object} params
 * @param {string} params.telegramUrl - Resolved https://t.me/<name> URL
 * @param {string} [params.context] - Where the click happened (e.g. 'voice_widget')
 */
export function trackTelegramClick({ telegramUrl, context }) {
  pushEvent('telegram_click', {
    sg_telegram_url: telegramUrl,
    sg_click_context: context,
  });

  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'telegram_button_click', {
        event_category: 'engagement',
        event_label: telegramUrl,
        value: 1,
      });
    }
  } catch {
    // gtag вызов не должен ломать основную логику
  }
}

/**
 * AI search-bar query submitted (Meilisearch instant search) — #1133.
 * Fires once per committed query; feeds the CS Analytics "AI Search Submits"
 * metric (browser_control_events → event_type='analytics', eventName='sg_search_submit').
 * @param {Object} params
 * @param {string} params.query - Trimmed search query
 * @param {number} [params.resultCount] - Number of results returned
 * @param {string} [params.variant] - Search bar variant ('full' | 'standalone' | 'compact')
 */
export function trackSearchSubmit({ query, resultCount, variant }) {
  pushEvent('search_submit', {
    sg_query: query,
    sg_result_count: resultCount,
    sg_variant: variant,
  });
}

/**
 * Lead form captured successfully (#1566) — the dealer's PPC conversion.
 * Call exactly once per captured submission.
 *
 * Unlike the `sg_` events above this is a contract with the dealer's ad
 * agency, so it uses their names: `lead_submitted` in the dataLayer (their GTM
 * triggers the GA4 lead tag on it) and the Meta pixel's standard `Lead` event,
 * fired directly so it needs no GTM change. `eventID` = our lead id, ready for
 * Conversions API dedup. No gtag() call here: the GA4 tag lives in their GTM,
 * a direct call would count the lead twice. No beacon either — LeadFormEmbed
 * already beacons its own funnel.
 *
 * @param {Object} params
 * @param {string} params.formType - Form type (check_availability, finance, …)
 * @param {string|null} [params.carId] - Car the enquiry is about
 * @param {string|null} [params.leadId] - CRM lead id returned by the capture
 */
export function trackLeadSubmitted({ formType, carId, leadId }) {
  try {
    pushToDataLayer({
      event: 'lead_submitted',
      form_type: formType,
      car_id: carId || undefined,
      lead_id: leadId || undefined,
    });
  } catch {
    // Analytics should never crash the widget
  }

  try {
    // shortcut: in iframe mode the host's pixel is in the parent, so no Lead is sent; forward it via iframeEventEmitter when an iframe dealer needs Meta.
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'Lead', { content_name: formType }, leadId ? { eventID: String(leadId) } : undefined);
    }
  } catch {
    // fbq вызов не должен ломать основную логику
  }
}
